import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@repo/ui/components/Base/Dialog";
import { Button } from "@repo/ui/components/Base/Button";
import type { Tenant, User } from "@repo/types";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { useGetTermsAndConditions } from "@/hooks/queryHooks/configHooks";
import { userRepo } from "@/repositories/userRepo";
import { tenantRepo } from "@/repositories/tenantRepo";

interface TermsAndConditionsDialogProps {
  open: boolean;
  setOpen: (arg: boolean) => void;
  user?: User;
  tenant?: Tenant;
  allowCancel?: boolean;
}

export const TermsAndConditionsDialog = ({
  open,
  setOpen,
  user: userData,
  tenant: tenantData,
  allowCancel = false,
}: TermsAndConditionsDialogProps) => {
  const { data, loaded } = useGetTermsAndConditions();

  const handleOpenChange = () => setOpen(!open);
  const handleCancel = () => setOpen(false);

  const handleAcceptTermsAndConditions = async () => {
    if (!data) return;

    if (userData) {
      await userRepo.update(userData.id, {
        termsAndConditionsAccepted: true,
        termsAndConditionsAcceptedAt: new Date(),
        termsAndConditionsAcceptedVersion: data.version,
      });
      handleOpenChange();
      return;
    }

    if (tenantData) {
      await tenantRepo.update(tenantData.id, {
        ownerTermsAndConditionsAccepted: true,
        ownerTermsAndConditionsAcceptedAt: new Date(),
        ownerTermsAndConditionsAcceptedVersion: data.version,
      });
      handleOpenChange();
      return;
    }
  };

  if (!loaded) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }

  if (!data) {
    return <div>Not Found</div>;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-screen-md"
        preventClickout
        preventEscape
        hideClose
      >
        <DialogHeader>
          <DialogTitle>Terms and Conditions</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <h1 className="text-lg">
            {userData?.termsAndConditionsAccepted ||
            tenantData?.ownerTermsAndConditionsAccepted
              ? "Please accept our updated terms and conditions to continue"
              : "Accept Terms and Conditions to continue"}
          </h1>
          <h1 className="text-2xl font-bold mb-2">Terms and Conditions</h1>
          {data && (
            <iframe
              src={data.url}
              title="Terms and Conditions"
              className="w-full h-[55vh]"
            />
          )}
        </DialogBody>
        <DialogFooter className="justify-between">
          {allowCancel && (
            <Button type="button" variant="destructive" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button type="button" onClick={handleAcceptTermsAndConditions}>
            Accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
